import React, { useState, useEffect, useCallback, useRef } from 'react';
import { widgetApi } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import {
  Plus, Trash2, LayoutGrid, Users, Calendar, DollarSign,
  Pill, AlertTriangle, TrendingUp, FileText, Briefcase, BarChart3,
  PieChart, Activity, GripVertical, Save, RotateCcw
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart as RPieChart, Pie, Cell, AreaChart, Area } from 'recharts';
import { toast } from 'sonner';
import { Responsive, useContainerWidth } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

const ICON_MAP = { Users, Calendar, DollarSign, Pill, AlertTriangle, TrendingUp, FileText, Briefcase, BarChart3, PieChart, Activity };
const COLORS = ['#0E7490', '#1A56DB', '#166534', '#92400E', '#991B1B', '#7C3AED', '#DB2777'];

export default function WidgetBuilderPage() {
  const [templates, setTemplates] = useState([]);
  const [widgets, setWidgets] = useState([]);
  const [widgetData, setWidgetData] = useState({});
  const [loading, setLoading] = useState(true);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showBuilder, setShowBuilder] = useState(false);
  const [layouts, setLayouts] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const containerRef = useRef(null);
  const { width: containerWidth } = useContainerWidth({ ref: containerRef });
  const [builderForm, setBuilderForm] = useState({
    name: '', widget_type: 'stat_card', data_source: 'patients',
    color: '#0E7490', size: 'medium', config: {}, filters: {}
  });

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    try {
      const [tplRes, dashRes] = await Promise.all([widgetApi.getTemplates(), widgetApi.getDashboard()]);
      setTemplates(tplRes.data.templates || []);
      const wdgs = dashRes.data.widgets || [];
      setWidgets(wdgs);
      // Generate initial layout from widget positions
      const lg = wdgs.map((w, i) => ({
        i: w.id,
        x: w.position?.x ?? (i % 3) * 4,
        y: w.position?.y ?? Math.floor(i / 3) * 3,
        w: w.position?.w ?? (w.size === 'small' ? 4 : w.size === 'large' ? 12 : 6),
        h: w.position?.h ?? 3,
        minW: 3, minH: 2
      }));
      setLayouts({ lg, md: lg, sm: lg.map(l => ({ ...l, w: 12, x: 0 })) });
      for (const w of wdgs) {
        try {
          const dataRes = await widgetApi.getData(w.widget_type, w.data_source);
          setWidgetData(prev => ({ ...prev, [w.id]: dataRes.data }));
        } catch (e) { console.error(`Widget ${w.id} data load failed`, e); }
      }
    } catch (err) {
      console.error('Failed to load widgets:', err);
    } finally {
      setLoading(false);
    }
  };

  const onLayoutChange = (layout) => {
    setHasChanges(true);
    setLayouts(prev => ({ ...prev, lg: layout }));
  };

  const saveLayout = async () => {
    const currentLayout = layouts.lg || [];
    for (const item of currentLayout) {
      const widget = widgets.find(w => w.id === item.i);
      if (widget) {
        try {
          await widgetApi.updateWidget(widget.id, { position: { x: item.x, y: item.y, w: item.w, h: item.h } });
        } catch (e) { console.error(`Widget ${widget.id} save failed`, e); }
      }
    }
    setHasChanges(false);
    toast.success('Layout saved');
  };

  const addFromTemplate = async (template) => {
    try {
      await widgetApi.addWidget({
        name: template.name, widget_type: template.widget_type,
        data_source: template.data_source, color: '#0E7490', size: 'medium',
        position: { x: 0, y: (layouts.lg?.length || 0) * 3, w: 6, h: 3 }
      });
      toast.success(`Added "${template.name}"`);
      setShowTemplates(false);
      loadAll();
    } catch (err) { toast.error('Failed to add'); }
  };

  const addCustomWidget = async () => {
    if (!builderForm.name.trim()) { toast.error('Enter a name'); return; }
    try {
      const w = builderForm.size === 'small' ? 4 : builderForm.size === 'large' ? 12 : 6;
      await widgetApi.addWidget({ ...builderForm, position: { x: 0, y: (layouts.lg?.length || 0) * 3, w, h: 3 } });
      toast.success(`Created "${builderForm.name}"`);
      setShowBuilder(false);
      setBuilderForm({ name: '', widget_type: 'stat_card', data_source: 'patients', color: '#0E7490', size: 'medium', config: {}, filters: {} });
      loadAll();
    } catch (err) { toast.error('Failed to create'); }
  };

  const removeWidget = async (id) => {
    try {
      await widgetApi.deleteWidget(id);
      setWidgets(prev => prev.filter(w => w.id !== id));
      setLayouts(prev => ({ ...prev, lg: (prev.lg || []).filter(l => l.i !== id) }));
      toast.success('Widget removed');
    } catch (err) { toast.error('Failed to remove'); }
  };

  const getWidgetLabel = (w) => {
    const typeLabels = { stat_card: 'KPI', chart_bar: 'Bar Chart', chart_pie: 'Pie Chart', chart_area: 'Area Chart', chart_line: 'Line Chart', progress: 'Progress', table: 'Table' };
    const sourceLabels = { patients: 'Patients', appointments: 'Appointments', claims: 'Claims', medications: 'Medications', vitals: 'Vitals', prescriptions: 'Prescriptions' };
    return `${typeLabels[w.widget_type] || w.widget_type} · ${sourceLabels[w.data_source] || w.data_source}`;
  };

  const renderWidgetContent = (widget) => {
    const data = widgetData[widget.id];
    if (!data) return <div className="flex items-center justify-center h-full text-sm text-[#94A3B8]">Loading...</div>;
    if (widget.widget_type === 'stat_card') {
      const value = data.total ?? data.today ?? data.total_prescriptions ?? 0;
      return (<div className="flex flex-col items-center justify-center h-full"><div className="text-4xl font-bold text-[#0A2342]">{typeof value === 'number' ? value.toLocaleString() : value}</div></div>);
    }
    if (widget.widget_type === 'chart_pie') {
      const chartData = data.breakdown ? Object.entries(data.breakdown).map(([k, v]) => ({ name: k.replace(/_/g, ' '), value: v })) : [];
      return (<ResponsiveContainer width="100%" height="90%"><RPieChart><Pie data={chartData} cx="50%" cy="50%" outerRadius={60} dataKey="value" label={({ value }) => value}>{chartData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Pie><Tooltip /></RPieChart></ResponsiveContainer>);
    }
    if (widget.widget_type === 'chart_bar') {
      const chartData = data.top_medications || (data.status_breakdown ? Object.entries(data.status_breakdown).map(([k, v]) => ({ name: k, count: v })) : []);
      return (<ResponsiveContainer width="100%" height="90%"><BarChart data={chartData}><CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" /><XAxis dataKey="name" fontSize={10} stroke="#64748B" /><YAxis fontSize={10} stroke="#64748B" /><Tooltip /><Bar dataKey="count" fill={widget.color || '#0E7490'} radius={[4, 4, 0, 0]} /></BarChart></ResponsiveContainer>);
    }
    if (widget.widget_type === 'chart_area' || widget.widget_type === 'chart_line') {
      const chartData = data.trends || [];
      return (<ResponsiveContainer width="100%" height="90%"><AreaChart data={chartData}><CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" /><XAxis dataKey="date" fontSize={10} stroke="#64748B" /><YAxis fontSize={10} stroke="#64748B" /><Tooltip /><Area type="monotone" dataKey="count" stroke={widget.color || '#0E7490'} fill={widget.color || '#0E7490'} fillOpacity={0.1} /></AreaChart></ResponsiveContainer>);
    }
    if (widget.widget_type === 'progress') {
      const rate = data.collection_rate || 0;
      return (<div className="flex flex-col items-center justify-center h-full"><div className="relative w-20 h-20"><svg className="w-20 h-20 -rotate-90"><circle cx="40" cy="40" r="34" fill="none" stroke="#E2E8F0" strokeWidth="6" /><circle cx="40" cy="40" r="34" fill="none" stroke={widget.color || '#0E7490'} strokeWidth="6" strokeDasharray={`${rate * 2.14} 214`} strokeLinecap="round" /></svg><div className="absolute inset-0 flex items-center justify-center text-lg font-bold text-[#0A2342]">{rate.toFixed(0)}%</div></div></div>);
    }
    return <div className="text-sm text-[#94A3B8] text-center mt-4">{widget.widget_type}</div>;
  };

  return (
    <div className="space-y-6" data-testid="widget-builder-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-semibold text-[#0A2342]" style={{ fontFamily: 'Outfit, sans-serif' }}>Widget Builder</h1>
          <p className="text-[#64748B] mt-1">Drag, resize, and arrange your dashboard widgets</p>
        </div>
        <div className="flex gap-2">
          {hasChanges && (
            <Button className="bg-[#166534] hover:bg-[#166534]/90 text-white animate-pulse" onClick={saveLayout} data-testid="save-layout-btn">
              <Save className="w-4 h-4 mr-2" /> Save Layout
            </Button>
          )}
          <Button variant="outline" onClick={() => setShowTemplates(true)} data-testid="add-template-btn">
            <LayoutGrid className="w-4 h-4 mr-2" /> Templates
          </Button>
          <Button className="bg-[#0E7490] hover:bg-[#0E7490]/90 text-white" onClick={() => setShowBuilder(true)} data-testid="custom-widget-btn">
            <Plus className="w-4 h-4 mr-2" /> Custom
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="w-10 h-10 border-4 border-[#0E7490] border-t-transparent rounded-full animate-spin" /></div>
      ) : widgets.length > 0 ? (
        <div ref={containerRef}>
        <Responsive
          className="layout"
          width={containerWidth || 1200}
          layouts={layouts}
          breakpoints={{ lg: 1200, md: 768, sm: 0 }}
          cols={{ lg: 12, md: 6, sm: 1 }}
          rowHeight={80}
          onLayoutChange={onLayoutChange}
          draggableHandle=".widget-drag-handle"
          isResizable={true}
          isDraggable={true}
          compactType="vertical"
          margin={[16, 16]}
        >
          {widgets.map(widget => (
            <div key={widget.id} data-testid={`widget-${widget.id}`}>
              <Card className="h-full careiq-card overflow-hidden group relative">
                {/* Widget Header — drag handle + label + controls */}
                <div className="widget-drag-handle flex items-center justify-between px-3 py-2 bg-[#F8FAFC] border-b border-[#E2E8F0] cursor-grab active:cursor-grabbing select-none">
                  <div className="flex items-center gap-2 min-w-0">
                    <GripVertical className="w-4 h-4 text-[#94A3B8] flex-shrink-0" />
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-[#0A2342] truncate">{widget.name}</div>
                      <div className="text-[10px] text-[#94A3B8]">{getWidgetLabel(widget)}</div>
                    </div>
                  </div>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => removeWidget(widget.id)} className="p-1 rounded bg-[#991B1B]/10 hover:bg-[#991B1B]/20" data-testid={`remove-widget-${widget.id}`}>
                      <Trash2 className="w-3 h-3 text-[#991B1B]" />
                    </button>
                  </div>
                </div>
                <CardContent className="p-3 h-[calc(100%-46px)]">
                  {renderWidgetContent(widget)}
                </CardContent>
              </Card>
            </div>
          ))}
        </Responsive>
        </div>
      ) : (
        <div className="text-center py-16 text-[#64748B]">
          <LayoutGrid className="w-16 h-16 mx-auto mb-4 text-[#94A3B8]" />
          <p className="text-lg font-medium">No widgets yet</p>
          <p className="text-sm mt-1">Add from templates or create custom ones</p>
          <Button className="mt-4 bg-[#0E7490] hover:bg-[#0E7490]/90 text-white" onClick={() => setShowTemplates(true)}>
            <Plus className="w-4 h-4 mr-2" /> Add Your First Widget
          </Button>
        </div>
      )}

      {/* Template Dialog */}
      <Dialog open={showTemplates} onOpenChange={setShowTemplates}>
        <DialogContent className="sm:max-w-xl" aria-describedby="tpl-desc">
          <DialogHeader>
            <DialogTitle>Widget Templates</DialogTitle>
            <p id="tpl-desc" className="text-sm text-[#64748B]">Choose a pre-built widget</p>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 mt-2 max-h-96 overflow-auto">
            {templates.map(tpl => {
              const Icon = ICON_MAP[tpl.icon] || BarChart3;
              return (
                <button key={tpl.id} onClick={() => addFromTemplate(tpl)} className="p-4 rounded-lg border border-[#E2E8F0] hover:border-[#0E7490] hover:bg-[#0E7490]/5 text-left transition-all" data-testid={`tpl-${tpl.id}`}>
                  <Icon className="w-5 h-5 text-[#0E7490] mb-2" />
                  <div className="text-sm font-medium text-[#0A2342]">{tpl.name}</div>
                  <div className="text-[10px] text-[#64748B] mt-1">{tpl.description}</div>
                </button>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      {/* Custom Builder Dialog */}
      <Dialog open={showBuilder} onOpenChange={setShowBuilder}>
        <DialogContent className="sm:max-w-md" aria-describedby="builder-desc">
          <DialogHeader>
            <DialogTitle>Build Custom Widget</DialogTitle>
            <p id="builder-desc" className="text-sm text-[#64748B]">Configure your widget</p>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label>Widget Name</Label>
              <Input value={builderForm.name} onChange={(e) => setBuilderForm({ ...builderForm, name: e.target.value })} placeholder="My Custom Widget" className="mt-1" data-testid="builder-name" />
            </div>
            <div>
              <Label>Chart Type</Label>
              <select value={builderForm.widget_type} onChange={(e) => setBuilderForm({ ...builderForm, widget_type: e.target.value })} className="w-full mt-1 rounded-md border border-[#E2E8F0] p-2 text-sm" data-testid="builder-type">
                <option value="stat_card">KPI Card</option>
                <option value="chart_bar">Bar Chart</option>
                <option value="chart_line">Line Chart</option>
                <option value="chart_pie">Pie Chart</option>
                <option value="chart_area">Area Chart</option>
                <option value="progress">Progress Ring</option>
              </select>
            </div>
            <div>
              <Label>Data Source</Label>
              <select value={builderForm.data_source} onChange={(e) => setBuilderForm({ ...builderForm, data_source: e.target.value })} className="w-full mt-1 rounded-md border border-[#E2E8F0] p-2 text-sm" data-testid="builder-source">
                <option value="patients">Patients</option>
                <option value="appointments">Appointments</option>
                <option value="claims">Claims</option>
                <option value="medications">Medications</option>
                <option value="vitals">Vitals</option>
                <option value="prescriptions">Prescriptions</option>
              </select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Color</Label>
                <div className="flex items-center gap-2 mt-1">
                  <input type="color" value={builderForm.color} onChange={(e) => setBuilderForm({ ...builderForm, color: e.target.value })} className="w-8 h-8 rounded cursor-pointer border-0" />
                  <Input value={builderForm.color} onChange={(e) => setBuilderForm({ ...builderForm, color: e.target.value })} className="font-mono text-xs" />
                </div>
              </div>
              <div>
                <Label>Size</Label>
                <select value={builderForm.size} onChange={(e) => setBuilderForm({ ...builderForm, size: e.target.value })} className="w-full mt-1 rounded-md border border-[#E2E8F0] p-2 text-sm" data-testid="builder-size">
                  <option value="small">Small (1/3)</option>
                  <option value="medium">Medium (1/2)</option>
                  <option value="large">Large (Full)</option>
                </select>
              </div>
            </div>
            <Button className="w-full bg-[#0E7490] hover:bg-[#0E7490]/90 text-white" onClick={addCustomWidget} data-testid="create-widget-btn">
              Create Widget
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
